import React, { useCallback, useEffect, useState } from 'react';
import {
  useConference, joinConference, startConference, conferenceApi, loadConferenceState,
} from '../services/conference';
import '../styles/Conference.css';

const fmtDate = d => new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtTime = d => new Date(d).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
const fmtClock = ms => new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function duration(a, b) {
  const m = Math.max(1, Math.round((new Date(b) - new Date(a)) / 60000));
  return m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60} мин`;
}

const STATUS_TEXT = {
  pending: 'Стенограмма в очереди…',
  processing: 'Стенограмма готовится…',
  done: 'Стенограмма готова',
  failed: 'Стенограмму сделать не удалось',
  none: 'Без записи',
};

// Окно «Начать конференцию»: выбор участников + запись
function StartModal({ onClose }) {
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [recording, setRecording] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    conferenceApi.candidates().then(setList).catch(() => setError('Не удалось загрузить список сотрудников'));
  }, []);

  const toggle = id => setPicked(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      await startConference([...picked], recording);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось начать конференцию');
      setBusy(false);
    }
  };

  return (
    <div className="conf-modal-bg" onClick={onClose}>
      <div className="conf-modal" onClick={e => e.stopPropagation()}>
        <div className="conf-modal-head">
          <b>Новая конференция</b>
          <button className="conf-x" onClick={onClose}>✕</button>
        </div>
        <p className="conf-hint">Кого пригласить? Им придёт уведомление, а в чате появится кнопка «Присоединиться».</p>
        {!list && !error && <div className="conf-muted">Загрузка…</div>}
        {list && list.length === 0 && (
          <div className="conf-muted">Ни у кого, кроме вас, нет доступа к «Конференц-залу». Его выдают в админке, в настройках пользователя чата.</div>
        )}
        <div className="conf-people">
          {(list || []).map(u => (
            <label key={u.id} className={`conf-person ${picked.has(u.id) ? 'on' : ''}`}>
              <input type="checkbox" checked={picked.has(u.id)} onChange={() => toggle(u.id)} />
              <span>{u.displayName}</span>
            </label>
          ))}
        </div>
        <label className="conf-rec-option">
          <input type="checkbox" checked={recording} onChange={e => setRecording(e.target.checked)} />
          <span>Записывать разговор и сделать стенограмму</span>
        </label>
        {error && <div className="conf-error">{error}</div>}
        <button className="conf-btn-primary conf-btn-wide" disabled={busy || picked.size === 0} onClick={start}>
          {busy ? 'Запускаю…' : `Начать конференцию${picked.size ? ` (${picked.size + 1} чел.)` : ''}`}
        </button>
      </div>
    </div>
  );
}

// Стенограмма одной конференции
function TranscriptModal({ conf, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    conferenceApi.transcript(conf.id).then(setData).catch(() => setError('Не удалось загрузить стенограмму'));
  }, [conf.id]);

  const start = new Date(conf.startedAt).getTime();
  const colors = {};
  const palette = ['#1565c0', '#2e7d32', '#ad1457', '#ef6c00', '#6a1b9a', '#00838f'];
  conf.participants.forEach((p, i) => { colors[p.userId] = palette[i % palette.length]; });

  return (
    <div className="conf-modal-bg" onClick={onClose}>
      <div className="conf-modal conf-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="conf-modal-head">
          <b>Стенограмма · {fmtDate(conf.startedAt)}, {fmtTime(conf.startedAt)}–{fmtTime(conf.endedAt)}</b>
          <button className="conf-x" onClick={onClose}>✕</button>
        </div>
        <div className="conf-muted">Участники: {conf.participants.map(p => p.displayName).join(', ')}</div>
        {error && <div className="conf-error">{error}</div>}
        {!data && !error && <div className="conf-muted">Загрузка…</div>}
        {data && (
          <>
            {data.hasAudio && (
              <audio className="conf-audio" controls preload="none" src={conferenceApi.audioUrl(conf.id)} />
            )}
            <a className="conf-download" href={conferenceApi.txtUrl(conf.id)}>⬇ Скачать текстом</a>
            {data.entries.length === 0 && (
              <div className="conf-muted">{data.error ? `Ошибка: ${data.error}` : 'Речи в записи не найдено.'}</div>
            )}
            <div className="conf-transcript">
              {data.entries.map((e, i) => (
                <div key={i} className="conf-line">
                  <span className="conf-line-time">{fmtClock(start + e.t * 1000)}</span>
                  <span className="conf-line-name" style={{ color: colors[e.userId] || '#333' }}>{e.name}:</span>{' '}
                  <span>{e.text}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConferencePage({ user }) {
  const { canCreate, active, call } = useConference();
  const [showStart, setShowStart] = useState(false);
  const [history, setHistory] = useState(null);
  const [openConf, setOpenConf] = useState(null);

  const loadHistory = useCallback(() => {
    conferenceApi.history().then(setHistory).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    loadConferenceState();
    loadHistory();
  }, [loadHistory]);

  // Пока что-то расшифровывается или идёт конференция — обновляем список
  const waiting = (history || []).some(c => c.transcriptStatus === 'pending' || c.transcriptStatus === 'processing');
  const activeKey = active.map(c => c.id).join(',');
  useEffect(() => {
    loadHistory();
  }, [activeKey, loadHistory]);
  useEffect(() => {
    if (!waiting) return undefined;
    const id = setInterval(loadHistory, 30000);
    return () => clearInterval(id);
  }, [waiting, loadHistory]);

  return (
    <div className="conf-page">
      <div className="conf-page-inner">
        {canCreate && !active.some(c => c.createdBy === user.id) && (
          <button className="conf-btn-primary conf-btn-big" onClick={() => setShowStart(true)}>
            🎥 Начать конференцию
          </button>
        )}

        {active.length > 0 && <h3 className="conf-h">Идёт сейчас</h3>}
        {active.map(c => (
          <div key={c.id} className="conf-card conf-card-live">
            <div>
              <b>Начата в {fmtTime(c.startedAt)}</b> · {c.creatorName}
              <div className="conf-muted">
                {c.participants.map(p => p.displayName).join(', ')}
                {c.recording ? ' · идёт запись' : ''}
              </div>
            </div>
            {call?.confId === c.id ? (
              <span className="conf-muted">Вы в конференции</span>
            ) : (
              <button className="conf-btn-primary" onClick={() => joinConference(c.id)}>Присоединиться</button>
            )}
          </div>
        ))}

        {!canCreate && active.length === 0 && (
          <div className="conf-empty">
            Сейчас конференций нет. Когда вас пригласят, придёт уведомление, а в чате появится кнопка «Присоединиться».
          </div>
        )}

        <h3 className="conf-h">{canCreate ? 'Прошедшие конференции и стенограммы' : 'Прошедшие конференции'}</h3>
        {history === null && <div className="conf-muted">Загрузка…</div>}
        {history && history.length === 0 && <div className="conf-muted">Пока не было.</div>}
        {(history || []).map(c => (
          <div key={c.id} className="conf-card">
            <div>
              <b>{fmtDate(c.startedAt)}, {fmtTime(c.startedAt)}–{c.endedAt ? fmtTime(c.endedAt) : '…'}</b>
              {c.endedAt && <span className="conf-muted"> · {duration(c.startedAt, c.endedAt)}</span>}
              <div className="conf-muted">
                {c.participants.map(p => p.joined ? p.displayName : `${p.displayName} (не зашёл)`).join(', ')}
              </div>
              {canCreate && (
                <div className={`conf-status conf-status-${c.transcriptStatus}`}>{STATUS_TEXT[c.transcriptStatus]}</div>
              )}
            </div>
            {canCreate && c.transcriptStatus === 'done' && (
              <button className="conf-btn-secondary" onClick={() => setOpenConf(c)}>Открыть</button>
            )}
            {canCreate && c.transcriptStatus === 'failed' && (
              <button className="conf-btn-secondary" onClick={() => setOpenConf(c)}>Подробнее</button>
            )}
          </div>
        ))}
      </div>

      {showStart && <StartModal onClose={() => setShowStart(false)} />}
      {openConf && <TranscriptModal conf={openConf} onClose={() => setOpenConf(null)} />}
    </div>
  );
}

export default ConferencePage;
